export type ConsentCategory = 'essential' | 'analytics' | 'marketing'

export interface ConsentState {
  essential: boolean
  analytics: boolean
  marketing: boolean
  recordId?: string
  version?: string
}

const STORAGE_KEY = 'cookie_consent'

function readConsent(): ConsentState | null {
  if (import.meta.server) return null
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    const state: ConsentState = {
      essential: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      recordId: typeof parsed.recordId === 'string' ? parsed.recordId : crypto.randomUUID(),
      version: typeof parsed.version === 'string' ? parsed.version : 'cmp-v1',
    }
    if (typeof parsed.recordId !== 'string') writeConsent(state)
    return state
  } catch {
    // Legacy format: 'accepted' or 'rejected'
    if (raw === 'accepted' || raw === 'rejected') {
      const granted = raw === 'accepted'
      const state: ConsentState = { essential: true, analytics: granted, marketing: granted,
        recordId: crypto.randomUUID(), version: 'cmp-v1' }
      writeConsent(state)
      return state
    }
    return null
  }
}

function writeConsent(state: ConsentState) {
  if (import.meta.server) return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

export function useCookieConsent() {
  const consent = useState<ConsentState | null>('cookie-consent', () => null)
  const hasConsented = computed(() => consent.value !== null)

  // Initialize from storage on client
  if (import.meta.client && consent.value === null) {
    consent.value = readConsent()
  }

  function acceptAll() {
    const state: ConsentState = { essential: true, analytics: true, marketing: true,
      recordId: consent.value?.recordId || crypto.randomUUID(), version: 'cmp-v1' }
    consent.value = state
    writeConsent(state)
    pushConsentToGTM(state)
  }

  function rejectAll() {
    const state: ConsentState = { essential: true, analytics: false, marketing: false,
      recordId: consent.value?.recordId || crypto.randomUUID(), version: 'cmp-v1' }
    consent.value = state
    writeConsent(state)
    pushConsentToGTM(state)
    revokeAnalyticsTracking()
    revokeAffiliateTracking()
  }

  function savePreferences(state: ConsentState) {
    const previouslyGranted = !!consent.value?.marketing
    const safe: ConsentState = { ...state, essential: true,
      recordId: consent.value?.recordId || crypto.randomUUID(), version: 'cmp-v1' }
    consent.value = safe
    writeConsent(safe)
    pushConsentToGTM(safe)
    if (!safe.analytics) revokeAnalyticsTracking()
    if (previouslyGranted && !safe.marketing) revokeAffiliateTracking()
  }

  function resetConsent() {
    if (import.meta.server) return
    localStorage.removeItem(STORAGE_KEY)
    consent.value = null
    revokeAnalyticsTracking()
    revokeAffiliateTracking()
  }

  function isGranted(category: ConsentCategory): boolean {
    if (!consent.value) return category === 'essential'
    return consent.value[category]
  }

  return {
    consent: readonly(consent),
    hasConsented,
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    isGranted,
  }
}

function revokeAnalyticsTracking() {
  if (import.meta.server) return
  const analyticsCookie = /^(?:_ga(?:_.+)?|_gid|_gat(?:_.+)?)$/
  const hostname = window.location.hostname
  const labels = hostname.split('.').filter(Boolean)
  const cookieDomains = new Set<string>([hostname])
  // Include registrable parent candidates (www.midigi.net -> midigi.net), but
  // never attempt to target the final public suffix alone.
  for (let index = 1; index < labels.length - 1; index += 1) {
    cookieDomains.add(labels.slice(index).join('.'))
  }
  for (const part of document.cookie.split(';')) {
    const name = part.split('=', 1)[0]?.trim()
    if (!name || !analyticsCookie.test(name)) continue
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
    for (const domain of cookieDomains) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}; SameSite=Lax`
    }
  }
}

function revokeAffiliateTracking() {
  if (import.meta.server) return
  useAffiliateAPI().revokeTracking().catch(() => {})
}

function pushConsentToGTM(state: ConsentState) {
  if (import.meta.server) return
  const w = window as any
  // GTM consent mode
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', {
      analytics_storage: state.analytics ? 'granted' : 'denied',
      ad_storage: state.marketing ? 'granted' : 'denied',
      ad_user_data: state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied',
    })
  }
  // GTM dataLayer event
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({
    event: 'cookie_consent_update',
    cookie_consent: {
      essential: true,
      analytics: state.analytics,
      marketing: state.marketing,
    },
  })
}
